import { SPOTIFY_CANVAS_ERROR_CODES, type SpotifyCanvasFileRefusal } from 'fym-shared/src/music24/spotifyCanvas';
import { canvasFileRule } from './spotifyCanvasRowRules';

/** The `cause` of a refused call: `customBaseQuery` keeps it as `m24Limit`. */
function causeOf(error: unknown): { code: string | null; reason: string | null } {
  const record = error as {
    m24Limit?: { code?: unknown; reason?: unknown };
    cause?: { code?: unknown; reason?: unknown };
  } | null;
  const cause = record?.m24Limit ?? record?.cause;
  return {
    code: typeof cause?.code === 'string' ? cause.code : null,
    reason: typeof cause?.reason === 'string' ? cause.reason : null,
  };
}

/** The code of a refused call, or null. */
export function canvasErrorCode(error: unknown): string | null {
  return causeOf(error).code;
}

/** What the row does after a refused POST (design spotify-canvas a2 note "POST errors"). */
export type CanvasPostOutcome =
  /** 409 M24_CANVAS_TERMS_REQUIRED: the terms dialog opens ("Accept and upload"). */
  | { kind: 'terms' }
  /** The row reads its state again and shows no error text. */
  | { kind: 'reload' }
  /** 403 M24_AUTO_UPLOAD_NOT_ENABLED: the row goes. */
  | { kind: 'hide' }
  /** 400 M24_CANVAS_FILE_REFUSED: the file sentence under the button. */
  | { kind: 'file'; rule: SpotifyCanvasFileRefusal | null }
  /** Any other error: "That did not work. Try again." */
  | { kind: 'generic' };

const RELOAD_CODES: readonly string[] = [
  SPOTIFY_CANVAS_ERROR_CODES.notConnected,
  SPOTIFY_CANVAS_ERROR_CODES.notReady,
  SPOTIFY_CANVAS_ERROR_CODES.noTrack,
  SPOTIFY_CANVAS_ERROR_CODES.alreadyLive,
];

export function canvasPostOutcome(error: unknown): CanvasPostOutcome {
  const { code, reason } = causeOf(error);
  if (code === SPOTIFY_CANVAS_ERROR_CODES.termsRequired) {
    return { kind: 'terms' };
  }
  if (code === SPOTIFY_CANVAS_ERROR_CODES.autoUploadNotEnabled) {
    return { kind: 'hide' };
  }
  if (code === SPOTIFY_CANVAS_ERROR_CODES.fileRefused) {
    return { kind: 'file', rule: canvasFileRule(reason) };
  }
  if (code !== null && RELOAD_CODES.includes(code)) {
    return { kind: 'reload' };
  }
  return { kind: 'generic' };
}
